import { useCallback, useState } from "react";

/**
 * The server's answer when it has no mobile number for the payer. Cashfree
 * needs one on every order, so the page asks for it and sends it with the
 * order as `payerPhone` (it is not saved to the account).
 */
export const PAYER_PHONE_REQUIRED = "ERR_PAYER_PHONE_REQUIRED";

// An Indian mobile number, with an optional +91 in front (the server's rule,
// payments/payer-contact.ts).
const MOBILE = /^(\+?91)?[6-9]\d{9}$/;

/** `value` without spaces and dashes when it is a mobile number, or null. */
export function payerMobile(value) {
  const compact = String(value ?? "").replaceAll(/[\s-]/g, "");
  return MOBILE.test(compact) ? compact : null;
}

/**
 * The payer's mobile number, asked for only once the server answered
 * ERR_PAYER_PHONE_REQUIRED (`askedFor`). `checked()` gives the number to
 * send: undefined while none was asked for, null (with the reason shown)
 * when what was typed is not a mobile number.
 */
export function usePayerPhone() {
  const [asked, setAsked] = useState(false);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(null);

  const askedFor = useCallback((failure) => {
    if (failure?.code !== PAYER_PHONE_REQUIRED) return false;
    setAsked(true);
    return true;
  }, []);

  const checked = useCallback(() => {
    if (!asked) return undefined;
    const mobile = payerMobile(phone);
    setError(mobile ? null : "Enter a 10-digit mobile number.");
    return mobile;
  }, [asked, phone]);

  return { asked, phone, setPhone, error, askedFor, checked };
}
